import { Directive, ElementRef, HostListener } from '@angular/core';
import { NgControl } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
/**
 * CurrencyFormatterDirective
 *
 * This directive formats an input field as a currency.
 * It is designed to work with Angular Reactive Forms (FormControl/FormGroup).
 * It handles focus, input restriction, and blur events.
 *
 * Usage :
 * <input type="text"  formControlName="Price"  appCurrencyFormatter/>
 *
 * Features:
 * - Automatically handles formatting on focus, input, and blur events.
 * - Prevents invalid characters from being entered.
 * - Ensures consistency between displayed value and FormControl value.
 */
@Directive({
    selector: '[appCurrencyFormatter]',
    standalone: true,
    providers: [CurrencyPipe]
})
export class CurrencyFormatterDirective {
    constructor(
        private el: ElementRef<HTMLInputElement>,
        private control: NgControl,
        private currencyPipe: CurrencyPipe
    ) { }

    /**
     * On focus:
     * - Shows the raw numeric value without the currency symbol.
     * - If the current value is 0, the input will appear empty for easy typing.
     *
     * Examples:
     * 1. Form control value = 1234.56 → input shows "1234.56"
     * 2. Form control value = 0       → input shows "" (empty string)
     * 3. Form control value = null    → input shows "" (empty string)
     */
    @HostListener('focus', ['$event'])
    onFocus(event: FocusEvent) {
        const input = this.el.nativeElement;
        const value = this.control.value || 0;
        input.value = value === 0 ? '' : value.toString();
    }

    /**
     * On input:
     * - Restricts input to numeric characters and a single decimal point.
     * - Limits the number of digits before and after the decimal.
     *
     * Rules:
     * - Max 7 digits before the decimal
     * - Max 2 digits after the decimal
     *
     * Examples:
     * - Typing "12345678.90" → input will be trimmed to "1234567.90"
     * - Typing "1234.5678" → input will be trimmed to "1234.56"
     */
    @HostListener('input', ['$event'])
    onInput(event: Event) {
        const input = this.el.nativeElement;
        // A single leading minus sign is a valid negative-amount indicator (see
        // numberOnlyWithNegative) - preserve it, then strip everything else down to digits/dot.
        const isNegative = input.value.startsWith('-');
        let value = input.value.replace(/[^0-9.]/g, '');

        // only one decimal point allowed
        const firstDotIndex = value.indexOf('.');
        if (firstDotIndex !== -1) {
            value =
                value.substring(0, firstDotIndex + 1) +
                value.substring(firstDotIndex + 1).replace(/\./g, '');
        }

        const parts = value.split('.');

        // remove leading zeros in integer part, but allow "0" if nothing else
        if (parts[0].length > 1) {
            parts[0] = parts[0].replace(/^0+/, '');
            if (parts[0] === '') parts[0] = '0';
        }

        // max 7 digits before decimal
        if (parts[0].length > 7) parts[0] = parts[0].slice(0, 7);
        // max 2 digits after decimal
        if (parts[1]?.length > 2) parts[1] = parts[1].slice(0, 2);
        // const finalValue = parts[1] !== undefined ? `${parts[0]}.${parts[1]}` : parts[0];
        const finalValue = (isNegative ? '-' : '') + parts.join('.');
        input.value = finalValue;
        this.control.control?.setValue(finalValue, { emitEvent: true });

    }

    /**
     * On blur:
     * - Formats the numeric value as a currency string using CurrencyPipe.
     * - Updates the underlying FormControl value as a number.
     *
     * Examples:
     * - Input "1234.5" → formats to "$1,234.50"
     * - Input "0"      → formats to "$0.00"
     * - Input "12345678.90" → trims to 7 digits before decimal → "$1,234,567.90"
     */
    @HostListener('blur', ['$event'])
    onBlur(event: FocusEvent) {
        const input = this.el.nativeElement;

        const isNegative = input.value.startsWith('-');
        let rawValue = input.value.replace(/[^0-9.]/g, '');
        const match = rawValue.match(/^(\d{0,7})(?:\.(\d{0,2}))?/);
        let formattedValue = '';

        if (match) {
            const integerPart = match[1] || '0';
            const decimalPart = match[2] || '';
            formattedValue = decimalPart
                ? `${integerPart}.${decimalPart}`
                : integerPart;
        }

        const numValue = (parseFloat(formattedValue) || 0) * (isNegative ? -1 : 1);
        this.control.control?.setValue(numValue);

        input.value =
            this.currencyPipe.transform(numValue, '$', 'symbol', '1.2-2') || '$0.00';
    }
}
